import { ContentType, SavedSearch, SavedSearchPage } from '@creditsights/api-ui-web';
import { Divider } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { isEmpty, sumBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { DEFAULT_DEBOUNCE_RATE, ROUTES } from '../../Base.constants';
import SavedSearchesDropdown from '../../components/SavedSearchesDropdown/SavedSearchesDropdown';
import SaveSearchButton from '../../components/SaveSearchButton/SaveSearchButton';
import useFetchSearch, { SearchContentType } from '../../hooks/searches/useFetchSearch';
import { Filters, PageType } from '../../models';
import { SortOption } from '../../services/services.constants';
import FiltersBar from '../../components/FiltersBar';
import { getSearchFilters, transformFilter } from '../../components/FiltersBar/constants';
import NoSearchResults from './NoSearchResults/NoSearchResults';
import {
  SEARCH_RESULT_DEFAULT_SORT_OPTION,
  SEARCH_RESULT_SORT_OPTIONS,
  SearchResultCategory,
  SEARCH_TERM_URL_KEY,
  DEFAULT_SECTION_ORDER_CONFIG,
  SECTION_ID,
  SearchResultCategoryToContentTypeFilterMap,
} from './SearchResults.constants';
import SearchResultsHeader from './SearchResultsHeader/SearchResultsHeader';
import ArticlesSearchResultTable from './searchResultsTables/ArticlesSearchResultTable/ArticlesSearchResultTable';
import CompaniesTable from './searchResultsTables/CompaniesTable/CompaniesTable';
import { SEARCH_RESULT_TABLES_PAGINATION } from './searchResultsTables/searchResultTables.logic';
import SortSelect from './SortSelect';
import { getUrlSearchParams } from './SearchResults.logic';
import { SELECTED_SAVED_SEARCH_URL_KEY } from '../../components/SavedSearchesDropdown/SavedSearchesDropdown.constants';
import styles from './SearchResults.module.scss';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';

const cx = classNames.bind(styles);

const SearchResults = (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get(SEARCH_TERM_URL_KEY)?.trim() || '';
  const activeSortOption: SortOption = (searchParams.get('sort') as SortOption) || SEARCH_RESULT_DEFAULT_SORT_OPTION;
  const validCategories = Object.values(SearchResultCategory);
  const activeCategory: SearchResultCategory =
    (searchParams.get('category') as SearchResultCategory) || SearchResultCategory.All;
  const activeFilterType = SearchResultCategoryToContentTypeFilterMap[activeCategory];
  const [filters, setFilters] = useState<Filters | null>(null);

  const setFiltersDebounced = useDebouncedCallback((filters: Filters | null) => {
    setFilters(filters || {});
  }, DEFAULT_DEBOUNCE_RATE);

  useEffect(() => {
    setPaginationByActiveCategory(activeCategory);
  }, [activeCategory]);

  const queriesEnabled = !!searchTerm && filters !== null;

  const [companiesShowItemsCount, setCompaniesShowItemsCount] = useState(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
  const [companiesPage, setCompaniesPage] = useState(1);
  const resetCompaniesPage = () => setCompaniesPage(1);
  const companiesQuery = useFetchSearch(
    SearchContentType.companies,
    filters,
    companiesShowItemsCount,
    companiesPage,
    searchTerm,
    undefined,
    queriesEnabled && [SearchResultCategory.All, SearchResultCategory.Companies].includes(activeCategory)
  );

  const [newsShowItemsCount, setNewsShowItemsCount] = useState(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
  const [newsPage, setNewsPage] = useState(1);
  const resetNewsPage = () => setNewsPage(1);
  const newsQuery = useFetchSearch(
    SearchContentType.news,
    filters,
    newsShowItemsCount,
    newsPage,
    searchTerm,
    activeSortOption,
    queriesEnabled && [SearchResultCategory.All, SearchResultCategory.News].includes(activeCategory)
  );

  const [covenantsShowItemsCount, setCovenantsShowItemsCount] = useState(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
  const [covenantsPage, setCovenantsPage] = useState(1);
  const resetCovenantsPage = () => setCovenantsPage(1);
  const covenantsQuery = useFetchSearch(
    SearchContentType.covenants,
    filters,
    covenantsShowItemsCount,
    covenantsPage,
    searchTerm,
    activeSortOption,
    queriesEnabled && [SearchResultCategory.All, SearchResultCategory.Covenants].includes(activeCategory)
  );

  const [researchShowItemsCount, setResearchShowItemsCount] = useState(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
  const [researchPage, setResearchPage] = useState(1);
  const resetResearchPage = () => setResearchPage(1);
  const researchQuery = useFetchSearch(
    SearchContentType.research,
    filters,
    researchShowItemsCount,
    researchPage,
    searchTerm,
    activeSortOption,
    queriesEnabled && [SearchResultCategory.All, SearchResultCategory.Research].includes(activeCategory)
  );

  useEffect(() => {
    resetCompaniesPage();
    resetNewsPage();
    resetCovenantsPage();
    resetResearchPage();
  }, [filters, searchTerm, activeCategory]);

  useEffect(() => {
    resetNewsPage();
    resetCovenantsPage();
    resetResearchPage();
  }, [activeSortOption]);

  const { data: userPermissions } = useFetchUserPermissions();

  const noResultsForAllCategories =
    filters !== null &&
    !companiesQuery.isLoading &&
    !companiesQuery.data?.length &&
    !researchQuery.isLoading &&
    !researchQuery.data?.length &&
    !covenantsQuery.isLoading &&
    !covenantsQuery.data?.length &&
    !newsQuery.isLoading &&
    !newsQuery.data?.length;
  const searchResultFailed =
    companiesQuery.isError || researchQuery.isError || covenantsQuery.isError || newsQuery.isError;

  const shouldDisplayNoResultsMessage =
    (!searchTerm || noResultsForAllCategories) && activeCategory === SearchResultCategory.All && !searchResultFailed;

  const resultsCount = useMemo(() => {
    const categoryQueryMap = {
      [SearchResultCategory.Companies]: companiesQuery,
      [SearchResultCategory.Research]: researchQuery,
      [SearchResultCategory.Covenants]: covenantsQuery,
      [SearchResultCategory.News]: newsQuery,
    };

    if (activeCategory === SearchResultCategory.All) {
      const queries = Object.values(categoryQueryMap);
      const isAnyCategoryLoading = queries.some((query) => query.isLoading);

      return isAnyCategoryLoading
        ? undefined
        : sumBy(Object.values(categoryQueryMap), (query) => query.totalResults ?? 0);
    }

    const activeQuery = categoryQueryMap[activeCategory];

    if (activeQuery?.isLoading) {
      return undefined;
    }

    return activeQuery?.totalResults ?? 0;
  }, [activeCategory, companiesQuery, researchQuery, covenantsQuery, newsQuery]);

  const resetTablesState = () => {
    setResearchShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
    setCompaniesShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
    setNewsShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
    setCovenantsShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultOption);
    resetResearchPage();
    resetCompaniesPage();
    resetNewsPage();
    resetCovenantsPage();
  };

  const setPaginationByActiveCategory = (activeCategory: string) => {
    switch (activeCategory) {
      case SearchResultCategory.Companies:
        setCompaniesShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultExtendedOption);
        resetCompaniesPage();
        break;
      case SearchResultCategory.News:
        setNewsShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultExtendedOption);
        resetNewsPage();
        break;
      case SearchResultCategory.Covenants:
        setCovenantsShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultExtendedOption);
        resetCovenantsPage();
        break;
      case SearchResultCategory.Research:
        setResearchShowItemsCount(SEARCH_RESULT_TABLES_PAGINATION.defaultExtendedOption);
        resetResearchPage();
        break;
      default:
        resetTablesState();
    }
  };

  const onSavedSearchSelect = (search: SavedSearch) => {
    // prevents duplicate api requests until filters are set after debounce
    setFilters(null);
    /* when "filters" and "dateRange" between different saved searches are the same
       FiltersBar component does not trigger "onChange".
       When we set filters to "null" loader is displayed until new filters are set.
       Therefore we fallback here to set filters to current value in case "onChange" is not triggered
       to prevent infinite loader.
    */
    setFiltersDebounced(filters);

    const newSearchParams = getUrlSearchParams(search);
    newSearchParams.set(SELECTED_SAVED_SEARCH_URL_KEY, search.id);
    setSearchParams(newSearchParams);
  };

  const onSortOptionSelect = (sortOptionValue: SortOption) => {
    searchParams.set('sort', sortOptionValue);
    setSearchParams(searchParams);
  };

  const onCategorySelect = (category: string) => {
    if (category !== activeCategory) {
      searchParams.set('category', category);
      setSearchParams(searchParams);
    }
  };

  if (!validCategories.includes(activeCategory)) {
    return <Navigate to={`${ROUTES.search}?searchTerm=${searchTerm}`} replace />;
  }

  if (isEmpty(searchTerm)) {
    return <Navigate to={ROUTES.notFound} replace />;
  }

  const renderSection = (sectionId: SECTION_ID) => {
    switch (sectionId) {
      case SECTION_ID.COMPANIES:
        return (
          <CompaniesTable
            key={sectionId}
            query={companiesQuery}
            showItemsCount={companiesShowItemsCount}
            onCategorySelect={() => {
              onCategorySelect(SearchResultCategory.Companies);
            }}
            onShowItemsCountChange={(count) => {
              setCompaniesShowItemsCount(count);
              resetCompaniesPage();
            }}
            activeCategory={activeCategory}
            tableCategory={SearchResultCategory.Companies}
            page={companiesPage}
            setPage={setCompaniesPage}
          />
        );
      case SECTION_ID.RESEARCH:
        return (
          <ArticlesSearchResultTable
            key={sectionId}
            contentType={ContentType.Research}
            query={researchQuery}
            showItemsCount={researchShowItemsCount}
            onShowItemsCountChange={(count) => {
              setResearchShowItemsCount(count);
              resetResearchPage();
            }}
            onCategorySelect={() => {
              onCategorySelect(SearchResultCategory.Research);
            }}
            activeCategory={activeCategory}
            tableCategory={SearchResultCategory.Research}
            page={researchPage}
            setPage={setResearchPage}
          />
        );
      case SECTION_ID.NEWS:
        return (
          <ArticlesSearchResultTable
            key={sectionId}
            contentType={ContentType.News}
            query={newsQuery}
            showItemsCount={newsShowItemsCount}
            onShowItemsCountChange={(count) => {
              setNewsShowItemsCount(count);
              resetNewsPage();
            }}
            onCategorySelect={() => {
              onCategorySelect(SearchResultCategory.News);
            }}
            activeCategory={activeCategory}
            tableCategory={SearchResultCategory.News}
            page={newsPage}
            setPage={setNewsPage}
          />
        );
      case SECTION_ID.COVENANTS:
        return (
          <ArticlesSearchResultTable
            key={sectionId}
            contentType={ContentType.Covenants}
            query={covenantsQuery}
            showItemsCount={covenantsShowItemsCount}
            onShowItemsCountChange={(count) => {
              setCovenantsShowItemsCount(count);
              resetCovenantsPage();
            }}
            onCategorySelect={() => {
              onCategorySelect(SearchResultCategory.Covenants);
            }}
            activeCategory={activeCategory}
            tableCategory={SearchResultCategory.Covenants}
            page={covenantsPage}
            setPage={setCovenantsPage}
          />
        );
      default: {
        return null;
      }
    }
  };
  const renderSearchResultSections = () => {
    const sectionIds = DEFAULT_SECTION_ORDER_CONFIG(userPermissions!.userSubscription);
    return sectionIds.map((sectionId) => renderSection(sectionId));
  };

  return (
    <div className={cx('body')}>
      <SearchResultsHeader
        searchTerm={searchTerm}
        onCategorySelect={onCategorySelect}
        activeCategory={activeCategory}
        resultsCount={resultsCount}
      />
      <div className={cx('toolbar', 'main-toolbar')}>
        <FiltersBar
          onChange={setFiltersDebounced}
          filterTypes={getSearchFilters(activeCategory, PageType.Search)}
          transformFilter={transformFilter}
          articleType={activeFilterType}
        />
        <div className={cx('toolbar', 'secondary-tools')}>
          <Divider vertical />
          <SortSelect
            options={SEARCH_RESULT_SORT_OPTIONS}
            selectedOption={activeSortOption}
            onOptionSelect={onSortOptionSelect}
          />
          <Divider vertical className={cx('save-tools-divider')} />
          <div className={cx('toolbar', 'save-tools')}>
            <SaveSearchButton
              filters={filters}
              searchTerm={searchTerm}
              tab={activeCategory}
              sort={activeSortOption}
              searchResultsCount={resultsCount}
              pageName={SavedSearchPage.GlobalSearch}
            />
            <SavedSearchesDropdown onSearchSelect={onSavedSearchSelect} pageName={SavedSearchPage.GlobalSearch} />
          </div>
        </div>
      </div>
      <div className={cx('widgets')}>
        {shouldDisplayNoResultsMessage && <NoSearchResults />}
        {searchTerm && renderSearchResultSections()}
      </div>
    </div>
  );
};

export default SearchResults;
